import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Search, X, Download } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

// Convert YYYY-MM-DD string → ms timestamp at local midnight.
function toStart(dateStr) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}
function toEnd(dateStr) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

// Preset ranges — start returns [from, to] YYYY-MM-DD strings.
const iso = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
    {
        label: "Last 30 days",
        v: "30d",
        range: () => {
            const to = new Date();
            const from = new Date();
            from.setDate(to.getDate() - 30);
            return [iso(from), iso(to)];
        },
    },
    {
        label: "This month",
        v: "month",
        range: () => {
            const now = new Date();
            const from = new Date(now.getFullYear(), now.getMonth(), 1);
            return [iso(from), iso(now)];
        },
    },
    {
        label: "Year to date",
        v: "ytd",
        range: () => {
            const now = new Date();
            const from = new Date(now.getFullYear(), 0, 1);
            return [iso(from), iso(now)];
        },
    },
];



export default function Payments() {
    const [orders, setOrders] = useState([]);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [query, setQuery] = useState("");

    const [vendorTransactions, setVendorTransactions] = useState([]);
    const [vendorTransactionsLoading, setVendorTransactionsLoading] =
        useState(true);
    const [vendorTransactionsError, setVendorTransactionsError] =
        useState("");
    const [selectedVendorTransaction, setSelectedVendorTransaction] =
        useState(null);

    useEffect(() => {
        api.get("/member/payments").then((r) => setOrders(r.data));
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function loadVendorTransactions() {
            try {
                setVendorTransactionsLoading(true);
                setVendorTransactionsError("");

                const response = await api.get(
                    "/member/vendor-referred-transactions"
                );

                if (!cancelled) {
                    const data = response.data;
                    setVendorTransactions(
                        Array.isArray(data) ? data : data.items || []
                    );
                }
            } catch (error) {
                if (!cancelled) {
                    setVendorTransactionsError(
                        "Unable to load vendor-referred transactions."
                    );
                }
            } finally {
                if (!cancelled) {
                    setVendorTransactionsLoading(false);
                }
            }
        }

        loadVendorTransactions();

        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        const fromTs = toStart(from);
        const toTs = toEnd(to);
        const q = query.trim().toLowerCase();
        return orders.filter((o) => {
            const ts = new Date(o.at).getTime();
            if (fromTs && ts < fromTs) return false;
            if (toTs && ts > toTs) return false;
            if (q) {
                const hay = [
                    o.deal_title,
                    o.vendor,
                    o.redeemed_by_name,
                    o.status,
                    o.card_last4 ? `•••• ${o.card_last4}` : "",
                    `${o.amount ?? ""}`,
                ]
                    .join(" ")
                    .toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [orders, from, to, query]);

    const totalSpend = filtered.reduce((s, o) => s + (o.amount || 0), 0);
    const active = from || to || query;

    const applyPreset = (v) => {
        const preset = PRESETS.find((p) => p.v === v);
        if (!preset) return;
        const [f, t] = preset.range();
        setFrom(f);
        setTo(t);
    };

    const clear = () => {
        setFrom("");
        setTo("");
        setQuery("");
    };

    const exportCSV = () => {
        const rows = [
            ["Order", "Vendor", "Redeemed by", "Amount", "Card", "Status", "Date"],
            ...filtered.map((o) => [
                o.deal_title,
                o.vendor,
                o.redeemed_by_name || "You",
                (o.amount ?? 0).toFixed(2),
                o.card_last4 ? `•••• ${o.card_last4}` : "",
                o.status,
                new Date(o.at).toLocaleDateString(),
            ]),
        ];
        const csv = rows
            .map((r) =>
                r
                    .map((c) => {
                        const s = String(c ?? "");
                        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    })
                    .join(",")
            )
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `perx-receipts-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <PageHeader
                eyebrow="Wallet"
                title="Payments & receipts"
                description="Every purchase you've made through Perx, receipts included."
                testid="payments-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-5">
                {orders.length > 0 && (
                    <div
                        data-testid="payments-filters"
                        className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-5"
                    >
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="flex-1 min-w-[220px]">
                                <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                    Search
                                </Label>
                                <div className="relative mt-1.5">
                                    <Search
                                        size={15}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-500"
                                    />
                                    <Input
                                        data-testid="payments-search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Deal, vendor, redeemer, card…"
                                        className="pl-9 bg-white border-navy-100 focus-visible:ring-gold-500 h-10"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                    From
                                </Label>
                                <Input
                                    type="date"
                                    value={from}
                                    onChange={(e) => setFrom(e.target.value)}
                                    max={to || undefined}
                                    data-testid="payments-from"
                                    className="mt-1.5 bg-white border-navy-100 focus-visible:ring-gold-500 h-10 w-44"
                                />
                            </div>
                            <div>
                                <Label className="text-navy-700 text-[10px] uppercase tracking-widest">
                                    To
                                </Label>
                                <Input
                                    type="date"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    min={from || undefined}
                                    data-testid="payments-to"
                                    className="mt-1.5 bg-white border-navy-100 focus-visible:ring-gold-500 h-10 w-44"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {PRESETS.map((p) => (
                                    <Button
                                        key={p.v}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => applyPreset(p.v)}
                                        data-testid={`payments-preset-${p.v}`}
                                        className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-10"
                                    >
                                        {p.label}
                                    </Button>
                                ))}
                                {active && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={clear}
                                        data-testid="payments-clear"
                                        className="text-navy-500 h-10"
                                    >
                                        <X size={14} className="mr-1" /> Clear
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-navy-100/70 flex items-center justify-between gap-4 flex-wrap">
                            <div
                                data-testid="payments-summary"
                                className="text-sm text-navy-500"
                            >
                                Showing{" "}
                                <span className="text-navy-700 font-semibold">
                                    {filtered.length}
                                </span>{" "}
                                of {orders.length} receipts · Total{" "}
                                <span className="text-navy-700 font-semibold">
                                    ${totalSpend.toFixed(2)}
                                </span>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={exportCSV}
                                disabled={filtered.length === 0}
                                data-testid="payments-export"
                                className="rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-9"
                            >
                                <Download size={14} className="mr-1.5" /> Export CSV
                            </Button>
                        </div>
                    </div>
                )}

                {orders.length === 0 ? (
                    <div className="rounded-2xl bg-white border border-navy-100/70 py-16 text-center">
                        <img
                            src="/panda/panda-table.png"
                            alt=""
                            className="h-32 mx-auto mb-4"
                        />
                        <div className="font-display text-2xl text-navy-700 mb-1">
                            No purchases yet.
                        </div>
                        <div className="text-navy-500 text-sm">
                            When you buy your first perk, it&apos;ll appear here.
                        </div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="rounded-2xl bg-white border border-navy-100/70 py-14 text-center">
                        <div className="font-display text-xl text-navy-700 mb-1">
                            No receipts match your filters.
                        </div>
                        <div className="text-navy-500 text-sm mb-4">
                            Try widening the date range or clearing the search.
                        </div>
                        <Button
                            variant="outline"
                            onClick={clear}
                            data-testid="payments-empty-clear"
                            className="rounded-full border-navy-100 text-navy-700"
                        >
                            Clear filters
                        </Button>
                    </div>
                ) : (
                    <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                        <Table data-testid="payments-table">
                            <TableHeader>
                                <TableRow className="bg-cream hover:bg-cream">
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Order</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Vendor</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Redeemed by</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Amount</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Card</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Status</TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((o) => (
                                    <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                                        <TableCell className="font-medium text-navy-700">
                                            {o.deal_title}
                                        </TableCell>
                                        <TableCell className="text-navy-500">{o.vendor}</TableCell>
                                        <TableCell>
                                            <Badge
                                                className={
                                                    o.redeemed_by_kind === "sub"
                                                        ? "bg-gold-500/10 text-gold-700 border-gold-200 border"
                                                        : "bg-navy-50 text-navy-700 border-navy-100 border"
                                                }
                                            >
                                                {o.redeemed_by_name || "You"}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-navy-700">
                                            ${o.amount.toFixed(2)}
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            •••• {o.card_last4}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className="bg-gold-500/10 text-gold-700 hover:bg-gold-500/10 border-gold-200">
                                                {o.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-navy-500">
                                            {new Date(o.at).toLocaleDateString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {/* Vendor-referred transactions */}
            <section
                data-testid="vendor-referred-transactions"
                className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden"
            >
                <div className="p-5 border-b border-navy-100/70">
                    <h2 className="font-display text-xl text-navy-700">
                        Vendor-referred transactions
                    </h2>
                    <p className="text-sm text-navy-500 mt-1">
                        Insurance enquiries, quotes and other transactions
                        associated with vendor referrals.
                    </p>
                </div>

                {vendorTransactionsLoading ? (
                    <div className="p-8 text-center text-sm text-navy-500">
                        Loading vendor-referred transactions...
                    </div>
                ) : vendorTransactionsError ? (
                    <div className="p-8 text-center">
                        <p className="text-sm text-red-600">
                            {vendorTransactionsError}
                        </p>
                        <Button
                            variant="outline"
                            className="mt-3 rounded-full"
                            onClick={() =>
                                window.location.reload()
                            }
                        >
                            Retry
                        </Button>
                    </div>
                ) : vendorTransactions.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="font-display text-xl text-navy-700 mb-1">
                            No vendor referrals yet.
                        </div>
                        <p className="text-sm text-navy-500">
                            Transactions associated with vendor referrals
                            will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-cream hover:bg-cream">
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Vendor
                                    </TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Product
                                    </TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Customer
                                    </TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Quote
                                    </TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Vendor share
                                    </TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Status
                                    </TableHead>
                                    <TableHead className="text-navy-700 uppercase text-xs tracking-widest">
                                        Date
                                    </TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {vendorTransactions.map((t) => {
                                    const quote = t.calculator_quote;
                                    const currency = (
                                        t.currency || "hkd"
                                    ).toUpperCase();

                                    return (
                                        <TableRow
                                            key={t.id || t._id}
                                            data-testid={`vendor-transaction-${t.id || t._id}`}
                                            className="cursor-pointer hover:bg-navy-50"
                                            onClick={() => setSelectedVendorTransaction(t)}
                                        >
                                            <TableCell className="font-medium text-navy-700">
                                                {t.vendor || "—"}
                                                <div className="text-xs text-navy-500 mt-1">
                                                    {t.vendor_email || ""}
                                                </div>
                                            </TableCell>

                                            <TableCell className="text-navy-700">
                                                {t.product_name || "—"}
                                            </TableCell>

                                            <TableCell className="text-navy-700">
                                                {t.member?.name || "—"}
                                                <div className="text-xs text-navy-500 mt-1">
                                                    {t.buyer_email || t.member?.email || ""}
                                                </div>
                                            </TableCell>

                                            <TableCell className="text-navy-700">
                                                {quote != null
                                                    ? `${currency} ${Number(quote).toLocaleString(
                                                        undefined,
                                                        {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        }
                                                    )}`
                                                    : "Pending"}
                                            </TableCell>

                                            <TableCell className="text-navy-700">
                                                {t.vendor_share_amount != null
                                                    ? `${currency} ${Number(
                                                        t.vendor_share_amount
                                                    ).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}`
                                                    : "Pending"}
                                                {t.vendor_share_pct != null && (
                                                    <div className="text-xs text-navy-500 mt-1">
                                                        {t.vendor_share_pct}% share
                                                    </div>
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Badge
                                                    className={
                                                        (t.job_status || "").toLowerCase() === "complete"
                                                            ? "bg-green-100 text-green-800 border border-green-300"
                                                            : "bg-gold-500/10 text-gold-700 border border-gold-200"
                                                    }
                                                >
                                                    {t.job_status || "Pending"}
                                                </Badge>
                                            </TableCell>

                                            <TableCell className="text-navy-500 whitespace-nowrap">
                                                {t.created_at
                                                    ? new Date(
                                                        t.created_at
                                                    ).toLocaleDateString()
                                                    : "—"}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </section>
            </div>
            {/* ATOMS */}

            <Dialog
                open={!!selectedVendorTransaction}
                onOpenChange={(open) => {
                    if (!open) setSelectedVendorTransaction(null);
                }}
            >
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-navy-800">
                            Transaction notes
                        </DialogTitle>
                        <DialogDescription>
                            {selectedVendorTransaction?.vendor || "Vendor"} —{" "}
                            {selectedVendorTransaction?.product_name || "Transaction"}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-navy-500">
                                    Status
                                </p>
                                <p className="mt-1 text-sm text-navy-800">
                                    {selectedVendorTransaction?.job_status || "Pending"}
                                </p>
                            </div>

                            <div>
                                <p className="text-xs uppercase tracking-widest text-navy-500">
                                    Date
                                </p>
                                <p className="mt-1 text-sm text-navy-800">
                                    {selectedVendorTransaction?.created_at
                                        ? new Date(
                                            selectedVendorTransaction.created_at
                                        ).toLocaleString()
                                        : "—"}
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-navy-100 pt-4">
                            <h3 className="text-sm font-semibold text-navy-800 mb-2">
                                Notes
                            </h3>

                            <div className="rounded-xl bg-cream p-4 text-sm text-navy-700 whitespace-pre-wrap break-words">
                                {selectedVendorTransaction?.notes?.trim() ||
                                    "No notes available for this transaction."}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
           
        </>
    );

    
}
